/**
 * Meta tag sanitization utilities to prevent XSS attacks
 * Provides safe HTML escaping and validation for meta tag content
 */

/**
 * HTML entities for escaping special characters
 */
const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
  '`': '&#x60;',
  '=': '&#x3D;'
};

/**
 * Regex to match characters that need HTML escaping
 */
const HTML_ESCAPE_REGEX = /[&<>"'`=\/]/g;

/**
 * Maximum length limits for different meta tag types
 */
export const META_LIMITS = {
  TITLE: 70,        // Google displays ~70 characters in search results
  DESCRIPTION: 160, // Google displays ~160 characters in search results
  KEYWORDS: 255,    // Reasonable limit for keywords
  AUTHOR: 100,      // Reasonable limit for author names
  GENERIC: 500      // Generic limit for other meta content
} as const;

/**
 * Escape HTML special characters to prevent XSS attacks
 * @param text - Text to escape
 * @returns Escaped HTML string
 */
export const escapeHtml = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }
  
  return text.replace(HTML_ESCAPE_REGEX, (match) => HTML_ENTITIES[match] || match);
};

/**
 * Remove potentially dangerous characters and sequences from meta content
 * @param text - Text to sanitize
 * @returns Sanitized text
 */
export const sanitizeMetaContent = (text: string): string => {
  if (typeof text !== 'string') {
    return '';
  }

  return text
    // Remove null bytes and control characters (except newline, tab, carriage return)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    // Remove potentially dangerous Unicode characters
    .replace(/[\u2028\u2029]/g, '') // Line separator, Paragraph separator
    // Normalize whitespace
    .replace(/\s+/g, ' ')
    // Trim whitespace
    .trim();
};

/**
 * Validate that meta content doesn't contain suspicious patterns
 * @param text - Text to validate
 * @returns True if text passes security validation
 */
export const validateMetaContent = (text: string): boolean => {
  if (typeof text !== 'string') {
    return false;
  }

  // Check for suspicious patterns that might indicate XSS attempts
  const suspiciousPatterns = [
    /<script/i,                    // Script tags
    /javascript:/i,                // JavaScript protocol
    /vbscript:/i,                  // VBScript protocol
    /data:/i,                      // Data URLs
    /on\w+\s*=/i,                 // Event handlers
    /<iframe/i,                    // IFrame tags
    /<object/i,                    // Object tags
    /<embed/i,                     // Embed tags
    /<link/i,                      // Link tags
    /<meta/i,                      // Nested meta tags
    /<style/i,                     // Style tags
    /expression\s*\(/i,            // CSS expressions
    /url\s*\(/i,                   // CSS url() functions
    /@import/i,                    // CSS imports
    /&\#/,                         // HTML entity attacks
    /\\u[0-9a-f]{4}/i,            // Unicode escape sequences
    /\\x[0-9a-f]{2}/i,            // Hex escape sequences
  ];

  return !suspiciousPatterns.some(pattern => pattern.test(text));
};

/**
 * Truncate text to specified length while preserving word boundaries
 * @param text - Text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (typeof text !== 'string' || text.length <= maxLength) {
    return text;
  }

  // Find the last space before the limit
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  // If there's a space, truncate there; otherwise, hard truncate
  if (lastSpace > 0 && lastSpace > maxLength * 0.8) {
    return truncated.substring(0, lastSpace).trim();
  }
  
  return truncated.trim();
};

/**
 * Comprehensive meta tag sanitization and validation
 * @param content - Content to sanitize
 * @param type - Type of meta content (affects length limits)
 * @returns Sanitized and validated content
 */
export const sanitizeAndValidateMetaContent = (
  content: string,
  type: keyof typeof META_LIMITS = 'GENERIC'
): string => {
  if (typeof content !== 'string') {
    return '';
  }

  // Step 1: Basic sanitization
  let sanitized = sanitizeMetaContent(content);
  
  // Step 2: Security validation
  if (!validateMetaContent(sanitized)) {
    console.warn('Meta content failed security validation:', content);
    return ''; // Return empty string for suspicious content
  }
  
  // Step 3: HTML escaping
  sanitized = escapeHtml(sanitized);
  
  // Step 4: Length truncation
  const maxLength = META_LIMITS[type];
  sanitized = truncateText(sanitized, maxLength);
  
  return sanitized;
};

/**
 * Sanitize structured data (JSON-LD) to prevent injection attacks
 * @param data - Structured data object
 * @returns Sanitized structured data
 */
export const sanitizeStructuredData = (data: unknown): object | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  try {
    // Convert to string and back to remove any functions or dangerous content
    const jsonString = JSON.stringify(data);
    
    // Validate JSON doesn't contain suspicious patterns
    if (!validateMetaContent(jsonString)) {
      console.warn('Structured data failed security validation');
      return null;
    }

    // Parse back to object
    const parsed = JSON.parse(jsonString);
    
    // Recursively sanitize string values
    return sanitizeObjectStrings(parsed);
  } catch (error) {
    console.warn('Failed to sanitize structured data:', error);
    return null;
  }
};

/**
 * Recursively sanitize string values in an object
 * @param obj - Object to sanitize
 * @returns Sanitized object
 */
const sanitizeObjectStrings = (obj: any): any => {
  if (typeof obj === 'string') {
    return sanitizeAndValidateMetaContent(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObjectStrings);
  }
  
  if (obj && typeof obj === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Sanitize both keys and values
      const sanitizedKey = sanitizeAndValidateMetaContent(key);
      if (sanitizedKey) {
        sanitized[sanitizedKey] = sanitizeObjectStrings(value);
      }
    }
    return sanitized;
  }
  
  return obj;
};

/**
 * Validate that a URL is safe for use in meta tags
 * @param url - URL to validate
 * @returns True if URL is safe
 */
export const validateMetaUrl = (url: string): boolean => {
  if (typeof url !== 'string') {
    return false;
  }

  try {
    const parsed = new URL(url);
    
    // Only allow HTTP and HTTPS protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }
    
    // Check for suspicious patterns in the URL
    return validateMetaContent(url);
  } catch {
    return false;
  }
};